import {
  Divider,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { BookOpenText, PencilSimpleLine, X } from "@phosphor-icons/react";
import { ANLoadingScreen, ANTooltip } from "@answersnow/pkg-theme";
import { useGetPayorDataByName } from "hooks/api/payors/useGetPayorDataByName";
import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import actions from "actions";
import { getAvailableInsuranceCodes } from "selectors";
import { booleanToDisplay } from "utils/displayUtils";
import DocumentRuleCard from "./DocumentRules/DocumentRuleCard";

type PayorOption = { id: string | number; name: string };

const ListElement = ({ title, value }: { title: string; value?: string }) => (
  <Stack direction="row" justifyContent="space-between" alignItems="center">
    <Typography variant="body2" sx={{ mr: 2 }}>
      {title}
    </Typography>
    <ANTooltip
      component={Typography}
      variant="body2"
      fontWeight={500}
      style={{ maxWidth: "70%", flexShrink: 0 }}
    >
      {value ? value : "-"}
    </ANTooltip>
  </Stack>
);

const CATALOG_PARAMS: Record<string, string> = {
  single_case: "Single case Agreement",
  group: "Group Contract",
};

const ASSESSMENT_BILLING_CAPTIONS: Record<string, string> = {
  INITIAL_ASSESSMENT: "This plan requires Assessment hours on the Initial Treatment Plan only",
  RE_ASSESSMENT_OR_90_DAYS:
    "This plan requires Assessment hours on 90-Day Progress Summaries and Re-Assessment Treatment Plans only",
  BOTH: "This plan requires Assessment hours on Initial Treatment Plans, 90-Day Progress Summaries, and Re-Assessment Treatment Plans",
  NEITHER: "This plan requires separate documentation for Assessment hours",
};

export default function InsurancePlanView({
  title,
  onClose,
  data,
  topEditActions,
}: {
  title: string;
  onClose: () => void;
  data: any;
  topEditActions?: Array<{ icon: React.ReactNode; text: string; action: () => void }>;
}) {
  const theme = useTheme();
  const [anchorFiltersEl, setAnchorFiltersEl] = React.useState<null | HTMLElement>(null);
  const openFilters = Boolean(anchorFiltersEl);
  const handleFiltersOpen = (event: React.MouseEvent<HTMLElement>) =>
    setAnchorFiltersEl(event.currentTarget);
  const handleFiltersClose = () => setAnchorFiltersEl(null);

  const dispatch = useDispatch();
  const availableCptCodes = useSelector(getAvailableInsuranceCodes) as any[];

  useEffect(() => {
    if (!availableCptCodes?.length) {
      dispatch(actions.getAllAvailableInsuranceCodes({}));
    }
  }, [availableCptCodes?.length, dispatch]);

  const resolveCodeLabel = (id: number | null | undefined): string => {
    if (!id) return "-";
    const found = availableCptCodes?.find((c: any) => c.id === id);
    return found?.insurance_code ?? "-";
  };

  const { payorData: enterPayorOptions, payorDataLoading: enterPayorDataLoading } =
    useGetPayorDataByName("enter");
  const { payorData: silnaPayorOptions, payorDataLoading: silnaPayorDataLoading } =
    useGetPayorDataByName("silna");
  const { payorData: candidPayorOptions, payorDataLoading: candidPayorDataLoading } =
    useGetPayorDataByName("candid");

  // Helper to get payor name by id from options
  const getPayorName = (
    options: PayorOption[] | null,
    id: string | number | undefined
  ): string | undefined => {
    if (!options || !id) return undefined;
    const found = options.find((opt) => String(opt.id) === String(id));
    return found ? found.name : typeof id === "string" ? id : String(id);
  };

  const menus: React.ReactNode[] = [];
  if (topEditActions) {
    topEditActions.forEach((item) => {
      menus.push(
        <MenuItem
          key={item.text}
          onClick={() => {
            handleFiltersClose();
            item.action();
          }}
        >
          <ListItemIcon>{item.icon}</ListItemIcon>
          {item.text}
        </MenuItem>
      );
    });
  }

  if (enterPayorDataLoading || silnaPayorDataLoading || candidPayorDataLoading)
    return <ANLoadingScreen />;

  return (
    <Stack p={7} gap={7}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Stack direction="row" gap={3} alignItems="center">
          <BookOpenText fontSize="20px" weight="duotone" color={theme.palette.primary.main} />
          <ANTooltip component={Typography} variant="subtitle1" style={{ maxWidth: "90%" }}>
            {title}
          </ANTooltip>
        </Stack>
        <Stack direction="row" gap={3}>
          <IconButton size="small" onClick={handleFiltersOpen}>
            <PencilSimpleLine weight="duotone" />
          </IconButton>
          <Menu
            id="edit-menu"
            anchorEl={anchorFiltersEl}
            open={openFilters}
            onClose={handleFiltersClose}
            MenuListProps={{ "aria-labelledby": "open-menu-button" }}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
          >
            {menus}
          </Menu>
          <IconButton size="small" onClick={onClose}>
            <X />
          </IconButton>
        </Stack>
      </Stack>
      <Stack gap={6}>
        <Stack gap="16px">
          <ListElement title="Insurance Company" value={data.insurance_name} />
          <ListElement title="Contract Type" value={CATALOG_PARAMS[data.contract_type]} />
        </Stack>
      </Stack>
      <Divider />
      <Stack gap={6}>
        <Stack gap="16px">
          <ListElement title="Name of Plan" value={data.name} />
          <ListElement title="Coverage State" value={data.coverage_state} />
          <ListElement title="Medicaid Plan" value={booleanToDisplay(data.is_medicaid)} />
          <ListElement
            title="Staffed with credentialing submission"
            value={booleanToDisplay(data.allows_backdating)}
          />
          <ListElement
            title="Plan requires 15 min monthly check-in"
            value={booleanToDisplay(data.requires_monthly_checkin)}
          />
          <ListElement
            title="Silna Payor"
            value={getPayorName(silnaPayorOptions, data.silna_payor_uuid)}
          />
          <ListElement
            title="Candid Payor"
            value={getPayorName(candidPayorOptions, data.candid_payer_id)}
          />
          <ListElement
            title="Enter Payor"
            value={getPayorName(enterPayorOptions, data.eh_payer_id)}
          />
          <ListElement title="Phone Number" value={data.phone} />
          <ListElement title="Address" value={data.address_1} />
          <ListElement title="Address 2" value={data.address_2} />
          <ListElement title="State" value={data.state} />
          <ListElement title="City" value={data.city} />
          <ListElement title="ZIP Code" value={data.zip} />
        </Stack>
      </Stack>
      <Divider />
      <Stack gap={4}>
        <Typography variant="subtitle2">Clinical document rules</Typography>
        {data.document_rules?.length > 0 && (
          <Stack gap={2}>
            {data.document_rules.map((rule: any) => (
              <DocumentRuleCard key={rule.id} rule={rule} />
            ))}
          </Stack>
        )}
        <ListElement
          title="Plan requires Social Determinant of Health Survey"
          value={booleanToDisplay(data.requires_sdoh_survey)}
        />
      </Stack>
      <Divider />
      <Stack gap={6}>
        <Typography variant="subtitle2">CPT Mapping</Typography>
        <Stack gap="16px">
          <Stack>
            <ListElement title="Assessment" value={resolveCodeLabel(data.assessment_cpt_code_id)} />
            {data.approve_assessment_billing_at &&
              ASSESSMENT_BILLING_CAPTIONS[data.approve_assessment_billing_at] && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontStyle="italic"
                  lineHeight={1.4}
                >
                  {ASSESSMENT_BILLING_CAPTIONS[data.approve_assessment_billing_at]}
                </Typography>
              )}
          </Stack>
          <ListElement
            title="Direct Therapy"
            value={resolveCodeLabel(data.direct_therapy_cpt_code_id)}
          />
          <ListElement
            title="Caregiver Training"
            value={resolveCodeLabel(data.caregiver_training_cpt_code_id)}
          />
          <ListElement
            title="Caregiver Mediated"
            value={resolveCodeLabel(data.caregiver_mediated_cpt_code_id)}
          />
          <ListElement
            title="Direct Protocol Modification"
            value={resolveCodeLabel(data.protocol_modification_direct_cpt_code_id)}
          />
          <ListElement
            title="Indirect Protocol Modification"
            value={resolveCodeLabel(data.protocol_modification_indirect_cpt_code_id)}
          />
        </Stack>
      </Stack>
    </Stack>
  );
}
